import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Search, MoreHorizontal, Gift, Stamp } from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import {
  listMenuItems,
  createMenuItem,
  updateMenuItem,
  deleteMenuItem,
  getMenuItemDependentDeals,
  listMenuCategories,
  getStampCard,
} from "@/lib/api/admin";
import { uploadStoreImage } from "@/lib/api/admin";
import type { MenuItem, BundleDeal, BuyXGetYDeal } from "@/lib/api/types";
import { DependentDealsList } from "../dialogs/DependentDealsList";
import { BundleDealDetailDialog } from "../dialogs/BundleDealDetailDialog";
import { MultiBuyDealDetailDialog } from "../dialogs/MultiBuyDealDetailDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PointRewardFormDialog } from "../dialogs/PointRewardFormDialog";
import type { RewardPrefill } from "../dialogs/PointRewardFormDialog";
import { StampRewardFormDialog } from "../dialogs/StampRewardFormDialog";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";
import { Pagination } from "@/components/Pagination";
import { formatCents } from "@/lib/utils/number";
import { MenuItemFormDialog } from "../dialogs/MenuItemFormDialog";
import { ManageCategoriesDialog } from "../dialogs/ManageCategoriesDialog";
import { ReorderItemsDialog } from "../dialogs/ReorderItemsDialog";

const PAGE_SIZE = 20;

// ─── Main MenuTab ─────────────────────────────────────────────────────────────

type MenuTabProps = {
  storeId: string;
  storeCategory: string;
  priceLimitBypass: boolean;
};

export function MenuTab({ storeId, storeCategory, priceLimitBypass }: MenuTabProps) {
  const user = useAdminAuth();
  const qc = useQueryClient();

  const [categoriesDialogOpen, setCategoriesDialogOpen] = useState(false);
  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [editingItem, setEditingItem] = useState<MenuItem | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<MenuItem | null>(null);
  const [bundleDetail, setBundleDetail] = useState<BundleDeal | null>(null);
  const [multiBuyDetail, setMultiBuyDetail] = useState<BuyXGetYDeal | null>(null);
  const [rewardPrefill, setRewardPrefill] = useState<RewardPrefill | null>(null);
  const [rewardFormType, setRewardFormType] = useState<"point" | "stamp" | null>(null);
  const [rewardFormKey, setRewardFormKey] = useState(0);

  const [searchInput, setSearchInput] = useState("");
  const [categoryInput, setCategoryInput] = useState("");
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [offset, setOffset] = useState(0);

  function handleSearch() {
    setSearch(searchInput);
    setCategoryId(categoryInput);
    setOffset(0);
  }

  const { data: itemsData, isLoading } = useQuery({
    queryKey: ["menu-items", storeId, search, categoryId, offset],
    queryFn: async () =>
      listMenuItems(user!, storeId, { search, categoryId, limit: PAGE_SIZE, offset }),
    enabled: !!user,
    refetchOnMount: "always",
  });

  const { data: categories = [], refetch: refetchCategories } = useQuery({
    queryKey: ["menu-categories", storeId],
    queryFn: async () => listMenuCategories(user!, storeId),
    enabled: !!user,
    refetchOnMount: "always",
  });

  const { data: stampCard } = useQuery({
    queryKey: ["stamp-card", storeId],
    queryFn: async () => getStampCard(user!, storeId),
    enabled: !!user,
    refetchOnMount: "always",
  });

  const items = itemsData?.data ?? [];
  const total = itemsData?.total ?? 0;
  const isFiltering = search !== "" || categoryId !== "";

  function invalidateItems() {
    void qc.invalidateQueries({ queryKey: ["menu-items", storeId] });
  }

  const createMutation = useMutation({
    mutationFn: async ({
      data,
      imageFile,
    }: {
      data: Partial<MenuItem>;
      imageFile: File | null;
    }) => {
      let image_url = data.image_url;
      if (imageFile) {
        const res = await uploadStoreImage(user!, storeId, imageFile, "menu_item");
        image_url = res.url;
      }
      return createMenuItem(user!, storeId, { ...data, image_url });
    },
    onSuccess: () => {
      invalidateItems();
      setFormOpen(false);
      toast.success("Menu item created.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
      imageFile,
    }: {
      id: string;
      data: Partial<MenuItem>;
      imageFile: File | null;
    }) => {
      let image_url = data.image_url;
      if (imageFile) {
        const res = await uploadStoreImage(user!, storeId, imageFile, "menu_item");
        image_url = res.url;
      }
      return updateMenuItem(user!, storeId, id, { ...data, image_url });
    },
    onSuccess: () => {
      invalidateItems();
      setFormOpen(false);
      setEditingItem(undefined);
      toast.success("Menu item updated.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const {
    data: dependentDeals,
    isLoading: dependentDealsLoading,
    isError: dependentDealsError,
  } = useQuery({
    queryKey: ["menu-item-dependent-deals", storeId, deleteTarget?.id],
    queryFn: async () => getMenuItemDependentDeals(user!, storeId, deleteTarget!.id),
    enabled: !!user && !!deleteTarget,
  });
  const bundles = dependentDeals?.bundle_deals ?? [];
  const multiBuys = dependentDeals?.buy_x_get_y_deals ?? [];
  const dependentCount = bundles.length + multiBuys.length;

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => deleteMenuItem(user!, storeId, id),
    onSuccess: () => {
      invalidateItems();
      void qc.invalidateQueries({ queryKey: ["bundle-deals", storeId] });
      void qc.invalidateQueries({ queryKey: ["multi-buy-deals", storeId] });
      setDeleteTarget(null);
      toast.success("Menu item deleted.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const formPending = createMutation.isPending || updateMutation.isPending;

  function handleOpenAdd() {
    setEditingItem(undefined);
    setFormKey((k) => k + 1);
    setFormOpen(true);
  }

  function handleOpenEdit(item: MenuItem) {
    setEditingItem(item);
    setFormKey((k) => k + 1);
    setFormOpen(true);
  }

  function buildPrefill(item: MenuItem): RewardPrefill {
    return {
      name: item.name,
      description: item.description ?? undefined,
      priceCents: item.price_cents,
      imageUrl: item.image_url ?? undefined,
    };
  }

  function handleMakePointReward(item: MenuItem) {
    setRewardPrefill(buildPrefill(item));
    setRewardFormType("point");
    setRewardFormKey((k) => k + 1);
  }

  function handleMakeStampReward(item: MenuItem) {
    if (!stampCard) {
      toast.error("Set up a stamp card first.");
      return;
    }
    setRewardPrefill(buildPrefill(item));
    setRewardFormType("stamp");
    setRewardFormKey((k) => k + 1);
  }

  function handleMakePointRewardFromForm(prefill: RewardPrefill) {
    setRewardPrefill(prefill);
    setRewardFormType("point");
    setRewardFormKey((k) => k + 1);
  }

  function handleMakeStampRewardFromForm(prefill: RewardPrefill) {
    if (!stampCard) {
      toast.error("Set up a stamp card first.");
      return;
    }
    setRewardPrefill(prefill);
    setRewardFormType("stamp");
    setRewardFormKey((k) => k + 1);
  }

  function handleSave(data: Partial<MenuItem>, imageFile: File | null) {
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data, imageFile });
    } else {
      createMutation.mutate({ data, imageFile });
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2">
        <Button size="sm" variant="secondary" onClick={() => setReorderDialogOpen(true)}>
          Reorder Items
        </Button>
        <Button size="sm" variant="secondary" onClick={() => setCategoriesDialogOpen(true)}>
          Manage Categories
        </Button>
        <Button size="sm" onClick={handleOpenAdd}>
          <Plus className="size-4 mr-1" /> Add Item
        </Button>
      </div>

      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search items..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="pl-8"
            />
          </div>
          {categories.length > 0 && (
            <select
              value={categoryInput}
              onChange={(e) => setCategoryInput(e.target.value)}
              className="h-9 w-full sm:w-auto rounded-md border border-input bg-background px-3 text-sm text-foreground shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
          <Button size="sm" variant="secondary" onClick={handleSearch} className="w-full sm:w-auto">
            Search
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner className="size-5 border-primary" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title="No menu items"
            description={
              isFiltering ? "No items match your search." : "Add an item to get started."
            }
          />
        ) : (
          <div className="space-y-2">
            {items.map((item) => (
              <Card
                key={item.id}
                onClick={() => handleOpenEdit(item)}
                className="cursor-pointer hover:border-primary/50"
              >
                <CardContent className="flex items-center gap-3 p-3">
                  {item.image_url && (
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="size-12 rounded object-cover shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm truncate">{item.name}</p>
                      <StatusBadge status={item.status} className="text-[10px]" />
                    </div>
                    <p className="text-xs text-muted-foreground">{formatCents(item.price_cents)}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-7"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal className="size-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem onClick={() => handleMakePointReward(item)}>
                          <Gift className="size-4 mr-2" />
                          Make Point Reward
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleMakeStampReward(item)}>
                          <Stamp className="size-4 mr-2" />
                          Make Stamp Reward
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 text-muted-foreground enabled:hover:text-destructive enabled:hover:bg-destructive/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget(item);
                      }}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {total > 0 && (
          <Pagination offset={offset} limit={PAGE_SIZE} total={total} onOffsetChange={setOffset} />
        )}

        <ConfirmDialog
          open={deleteTarget !== null}
          onOpenChange={(open) => !open && setDeleteTarget(null)}
          title={dependentCount > 0 ? "Delete item and associated deals?" : "Delete menu item"}
          description={
            dependentDealsLoading ? (
              <p>Checking for associated deals...</p>
            ) : dependentDealsError ? (
              <p className="text-destructive">
                Couldn&apos;t check for associated deals. Close this dialog and try again.
              </p>
            ) : dependentCount > 0 ? (
              <DependentDealsList
                bundles={bundles}
                multiBuys={multiBuys}
                onSelectBundle={setBundleDetail}
                onSelectMultiBuy={setMultiBuyDetail}
              />
            ) : (
              `Delete "${deleteTarget?.name}"?`
            )
          }
          confirmLabel={dependentCount > 0 ? `Delete Item and ${dependentCount} Deals` : "Delete"}
          destructive
          isPending={deleteMutation.isPending}
          confirmDisabled={dependentDealsLoading || dependentDealsError}
          onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        />
        <BundleDealDetailDialog
          open={!!bundleDetail}
          onOpenChange={(open) => !open && setBundleDetail(null)}
          deal={bundleDetail}
        />
        <MultiBuyDealDetailDialog
          open={!!multiBuyDetail}
          onOpenChange={(open) => !open && setMultiBuyDetail(null)}
          deal={multiBuyDetail}
        />
      </div>

      <ManageCategoriesDialog
        open={categoriesDialogOpen}
        onOpenChange={setCategoriesDialogOpen}
        storeId={storeId}
        categories={categories}
        onRefresh={() => void refetchCategories()}
      />

      <ReorderItemsDialog
        open={reorderDialogOpen}
        onOpenChange={setReorderDialogOpen}
        storeId={storeId}
        categories={categories}
      />

      <MenuItemFormDialog
        key={formKey}
        open={formOpen}
        onOpenChange={setFormOpen}
        storeId={storeId}
        storeCategory={storeCategory}
        priceLimitBypass={priceLimitBypass}
        categories={categories}
        item={editingItem}
        onSave={handleSave}
        isPending={formPending}
        onMakePointReward={handleMakePointRewardFromForm}
        onMakeStampReward={handleMakeStampRewardFromForm}
      />

      <PointRewardFormDialog
        key={`point-${rewardFormKey}`}
        open={rewardFormType === "point"}
        onClose={() => setRewardFormType(null)}
        storeId={storeId}
        priceLimitBypass={priceLimitBypass}
        storeCategory={storeCategory}
        prefill={rewardPrefill ?? undefined}
      />

      <StampRewardFormDialog
        key={`stamp-${rewardFormKey}`}
        open={rewardFormType === "stamp"}
        onClose={() => setRewardFormType(null)}
        storeId={storeId}
        priceLimitBypass={priceLimitBypass}
        storeCategory={storeCategory}
        hasStampCard={stampCard !== null}
        prefill={rewardPrefill ?? undefined}
      />
    </div>
  );
}
